/**
 * AdvisorFixedAssetsTab — SB-Sicht auf die Anlagen eines Mandanten
 *
 * Feb 2026 — AfA-Modul Phase 4 gemäß /app/memory/KONZEPT_AFA_2026.md
 *
 * Features:
 *  - Liste aller Anlagen (active/disposed)
 *  - Detail-Sheet mit AfA-Plan + § 15a-Log
 *  - Edit-Mode bei `canEdit=true` (Vollmacht): ND/Methode/Privat-Anteil ändern → Re-Spread
 *  - Lock pro Anlage + Lock-Year-All-Button
 *  - Audit-Trail wird vom Backend automatisch geschrieben
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Lock, Unlock, Loader2, Boxes, AlertCircle, Calendar, Edit3,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const H = () => ({ Authorization: `Bearer ${localStorage.getItem("advisor_token") || ""}` });

const METHOD_LABELS = {
  linear: "Linear",
  degressive: "Degressiv",
  pool_5y: "Pool 5J",
  gwg_immediate: "GWG",
};

export default function AdvisorFixedAssetsTab({ clientId, canEdit }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [yearLockOpen, setYearLockOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/advisor/clients/${clientId}/fixed-assets`, { headers: H() });
      setAssets(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlagen konnten nicht geladen werden");
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const kpi = useMemo(() => {
    const active = assets.filter((a) => a.status === "active");
    const totalCost = active.reduce((s, a) => s + (a.cost_net || 0), 0);
    const yearlyAfa = active.reduce((s, a) => s + (a.cost_net || 0) / (a.useful_life_years || 1), 0);
    const locked = active.filter((a) => a.locked_by_advisor || a.locked_by_year_closure).length;
    return { count: active.length, totalCost, yearlyAfa, locked };
  }, [assets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="advisor-anlagen-tab">
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Aktive Anlagen" value={kpi.count} />
        <KpiCard label="Anschaffungswert" value={`${kpi.totalCost.toFixed(0)} €`} />
        <KpiCard label="Jahres-AfA" value={`${kpi.yearlyAfa.toFixed(0)} €`} />
        <KpiCard label="Gesperrt" value={kpi.locked} />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          {canEdit ? "✓ Vollmacht: Bearbeitung & Sperren möglich" : "Read-Only-Zugang (keine Vollmacht)"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const year = new Date().getFullYear();
              const url = `${API}/advisor/clients/${clientId}/fixed-assets/anlagenspiegel/${year}.pdf`;
              const token = localStorage.getItem("advisor_token") || "";
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.ok ? r.blob() : Promise.reject(r.statusText))
                .then((blob) => {
                  const u = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = u;
                  a.download = `Anlagenspiegel_${year}.pdf`;
                  a.click();
                  URL.revokeObjectURL(u);
                })
                .catch((err) => toast.error("PDF-Download fehlgeschlagen: " + err));
            }}
            data-testid="sb-anlagenspiegel-download-btn"
          >
            Anlagenspiegel-PDF
          </Button>
          {canEdit && assets.some((a) => a.status === "active") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setYearLockOpen(true)}
              data-testid="lock-year-btn"
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              Jahr-Lock im Periodenabschluss
            </Button>
          )}
        </div>
      </div>

      {/* Liste */}
      {assets.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500 text-sm">
            Mandant hat noch keine Anlagen erfasst.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Bezeichnung</th>
                  <th className="px-4 py-3 text-left">Anschaffung</th>
                  <th className="px-4 py-3 text-right">AK netto</th>
                  <th className="px-4 py-3 text-right">Jahres-AfA</th>
                  <th className="px-4 py-3 text-left">Methode</th>
                  <th className="px-4 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => {
                  const annual = (a.cost_net || 0) / (a.useful_life_years || 1);
                  return (
                    <tr
                      key={a.id}
                      onClick={() => setSelected(a)}
                      className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                      data-testid={`sb-asset-row-${a.id}`}
                    >
                      <td className="px-4 py-2.5 font-medium text-slate-800">
                        <div className="flex items-center gap-2">
                          {a.name}
                          {a.locked_by_advisor && <Lock className="h-3 w-3 text-rose-500" />}
                          {a.locked_by_year_closure && (
                            <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                              Jahr {a.locked_by_year_closure}
                            </Badge>
                          )}
                          {a.private_use_percentage > 0 && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                              {a.private_use_percentage}% privat
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{a.acquisition_date}</div>
                        <div className="text-slate-400">{a.useful_life_years} J</div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{a.cost_net?.toFixed(2)} €</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{annual.toFixed(2)} €</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs">{METHOD_LABELS[a.depreciation_method] || a.depreciation_method}</Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        {a.status === "active" ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Aktiv</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200">Veräußert</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Detail-Sheet */}
      <SbAssetDetailSheet
        asset={selected}
        clientId={clientId}
        canEdit={canEdit}
        onClose={() => setSelected(null)}
        onUpdated={load}
      />

      {/* Year-Lock-Dialog */}
      <YearLockDialog
        open={yearLockOpen}
        onClose={() => setYearLockOpen(false)}
        clientId={clientId}
        years={[...new Set(assets.map((a) => parseInt((a.acquisition_date || "")[0] || 0) > 0 ? parseInt(a.acquisition_date.slice(0, 4)) : 0).filter(Boolean))].sort()}
        onLocked={load}
      />
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

function SbAssetDetailSheet({ asset, clientId, canEdit, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [lockReason, setLockReason] = useState("");

  useEffect(() => {
    if (!asset) { setDetail(null); return; }
    axios
      .get(`${API}/advisor/clients/${clientId}/fixed-assets/${asset.id}`, { headers: H() })
      .then((res) => {
        setDetail(res.data);
        const a = res.data.asset;
        setForm({
          useful_life_years: a.useful_life_years,
          depreciation_method: a.depreciation_method,
          private_use_percentage: a.private_use_percentage || 0,
          residual_value: a.residual_value || 0,
        });
      })
      .catch(() => setDetail(null));
  }, [asset, clientId]);

  const handleSave = async () => {
    if (!reason || reason.trim().length < 5) {
      toast.error("Begründung erforderlich (min. 5 Zeichen)");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/advisor/clients/${clientId}/fixed-assets/${asset.id}`, {
        ...form,
        sb_change_reason: reason,
      }, { headers: H() });
      toast.success("Korrektur gespeichert + Audit-Log geschrieben");
      setEditing(false);
      setReason("");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/advisor/clients/${clientId}/fixed-assets/${asset.id}/lock`,
        { reason: lockReason || "Vom Steuerberater geprüft und festgeschrieben" },
        { headers: H() },
      );
      toast.success("Anlage gesperrt");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sperren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/fixed-assets/${asset.id}/unlock`, {}, { headers: H() });
      toast.success("Sperre aufgehoben");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Entsperren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;
  const a = detail?.asset || asset;
  const postings = detail?.postings || [];

  return (
    <Sheet open={!!asset} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="sb-asset-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-emerald-600" />
            {a.name}
            {a.locked_by_advisor && (
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                <Lock className="h-3 w-3 mr-1" /> Gesperrt
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 pt-4 pb-8">
          {/* Stammdaten */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 uppercase">Stammdaten</div>
            <div className="p-3 space-y-1.5 text-sm">
              <Row label="Anschaffung" value={a.acquisition_date} />
              <Row label="AK netto" value={`${a.cost_net?.toFixed(2)} €`} />
              <Row label="Vorsteuer" value={`${a.vat_amount?.toFixed(2)} €`} />
              <Row label="Kategorie" value={a.category_key} />
            </div>
          </div>

          {/* AfA-Parameter (editierbar bei canEdit) */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 uppercase flex items-center justify-between">
              <span>AfA-Parameter</span>
              {canEdit && !editing && !a.locked_by_advisor && !a.locked_by_year_closure && (
                <button onClick={() => setEditing(true)} className="text-emerald-600 hover:text-emerald-800" data-testid="sb-edit-btn">
                  <Edit3 className="h-3 w-3 inline mr-1" /> Korrigieren
                </button>
              )}
            </div>
            <div className="p-3 space-y-3 text-sm">
              {editing ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nutzungsdauer (Jahre)</Label>
                      <Input type="number" min={1} max={50}
                             value={form.useful_life_years}
                             onChange={(e) => setForm({ ...form, useful_life_years: parseInt(e.target.value) || 1 })}
                             data-testid="sb-edit-nd" />
                    </div>
                    <div>
                      <Label>Methode</Label>
                      <Select value={form.depreciation_method} onValueChange={(v) => setForm({ ...form, depreciation_method: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="linear">Linear</SelectItem>
                          <SelectItem value="degressive">Degressiv</SelectItem>
                          <SelectItem value="pool_5y">Pool 5J</SelectItem>
                          <SelectItem value="gwg_immediate">GWG</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Privat-Anteil: {form.private_use_percentage} %</Label>
                    <Slider value={[form.private_use_percentage]}
                            onValueChange={(v) => setForm({ ...form, private_use_percentage: v[0] })}
                            min={0} max={99} step={5} />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-amber-600" />
                      Begründung (Pflicht — wird im Audit-Log gespeichert)
                    </Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                              placeholder="z.B. BMF-Tabelle 2024 → 5 J statt 3 J realistischer"
                              data-testid="sb-edit-reason" />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                    Bei Änderung von ND/Methode wird der AfA-Plan ab dem nächsten Jahr neu berechnet. Bereits gebuchte Postings bleiben unverändert (GoBD).
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={saving || !reason} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sb-save-btn">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Korrektur speichern
                    </Button>
                    <Button variant="outline" onClick={() => { setEditing(false); setReason(""); }} disabled={saving}>Abbrechen</Button>
                  </div>
                </>
              ) : (
                <>
                  <Row label="Nutzungsdauer" value={`${a.useful_life_years} Jahre`} />
                  <Row label="Methode" value={METHOD_LABELS[a.depreciation_method] || a.depreciation_method} />
                  <Row label="Privat-Anteil" value={`${a.private_use_percentage || 0} %`} />
                  <Row label="Restwert" value={`${a.residual_value?.toFixed(2) || 0} €`} />
                </>
              )}
            </div>
          </div>

          {/* AfA-Plan */}
          {postings.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 uppercase">AfA-Plan</div>
              <table className="w-full text-sm">
                <thead className="bg-white text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Jahr</th>
                    <th className="px-3 py-1.5 text-right">AfA</th>
                    <th className="px-3 py-1.5 text-right">Buchwert</th>
                    <th className="px-3 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {postings.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-medium">{p.fiscal_year}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{p.amount?.toFixed(2)} €</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{p.book_value_after?.toFixed(2)} €</td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline" className="text-xs">{p.status === "posted" ? "Gebucht" : "Geplant"}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Lock / Unlock */}
          {canEdit && !editing && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2">
              <div className="text-xs font-medium text-slate-600 uppercase">Sperre</div>
              {a.locked_by_advisor ? (
                <>
                  <div className="text-xs text-rose-700">
                    🔒 Gesperrt am {a.locked_at?.slice(0, 10)} — Begründung: {a.locked_reason || "—"}
                  </div>
                  <Button variant="outline" size="sm" onClick={handleUnlock} disabled={saving} data-testid="sb-unlock-btn">
                    <Unlock className="h-3.5 w-3.5 mr-1.5" /> Sperre aufheben
                  </Button>
                </>
              ) : (
                <>
                  <Input
                    placeholder="Lock-Begründung (optional)"
                    value={lockReason}
                    onChange={(e) => setLockReason(e.target.value)}
                    className="text-sm"
                    data-testid="sb-lock-reason"
                  />
                  <Button variant="outline" size="sm" onClick={handleLock} disabled={saving} className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="sb-lock-btn">
                    <Lock className="h-3.5 w-3.5 mr-1.5" /> Anlage sperren
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value}</span>
    </div>
  );
}

function YearLockDialog({ open, onClose, clientId, years, onLocked }) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && years.length > 0) setSelectedYear(years[years.length - 1]);
  }, [open, years]);

  const handleLock = async () => {
    if (!selectedYear) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/advisor/clients/${clientId}/fixed-assets/lock-year/${selectedYear}`,
        {},
        { headers: H() },
      );
      toast.success(`${res.data.locked_count} Anlage(n) für ${selectedYear} gesperrt`);
      onLocked?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sperren fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Jahres-Lock aller Anlagen</DialogTitle>
          <DialogDescription>
            Sperrt alle aktiven Anlagen mit Anschaffungs-Jahr=X auf einmal. Verwendet im Periodenabschluss.
            Mandant kann diese Anlagen nicht mehr ändern, bis du den Lock aufhebst.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Jahrgang wählen</Label>
            <Select value={String(selectedYear || "")} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger data-testid="year-lock-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button onClick={handleLock} disabled={saving || !selectedYear} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-year-lock">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            {selectedYear} sperren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
